import React from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    TextField,
    Typography
} from '@material-ui/core';
import { IconEdit, IconTrash } from '@tabler/icons';
import { CRM_UPDATES_CHANGED, ROLE_OPTIONS, SEGMENT_OPTIONS } from '../crmUpdatesAccess';

// Profile → Publish CRM Updates (9719836187 only, server-enforced). Publishes
// items to the running "What's new" strip (CrmUpdateStrip.js).
const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const API = `${AI_PATH}/api/ai/announcements`;
const EMPTY = { text: '', tag: 'New', roles: [], segments: [] };
// Server shows each item for 24h from createdAt (crmAnnouncements.js SHOW_FOR_MS).
const SHOW_FOR_MS = 24 * 60 * 60 * 1000;
const isExpired = (i) => Date.now() - new Date(i.createdAt).getTime() > SHOW_FOR_MS;

const roleLabel = (v) => ROLE_OPTIONS.find((r) => r.value === v)?.label || `Role ${v}`;
const segLabel = (v) => SEGMENT_OPTIONS.find((s) => s.value === v)?.label || v;
const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const notify = () => window.dispatchEvent(new Event(CRM_UPDATES_CHANGED));

const CrmUpdatesManager = ({ open, onClose }) => {
    const [items, setItems] = React.useState([]);
    const [form, setForm] = React.useState(EMPTY);
    const [editId, setEditId] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');

    const load = React.useCallback(async () => {
        try {
            const res = await axios.get(API);
            setItems(res.data?.items || []);
        } catch (e) { setError(e.response?.data?.message || e.message); }
    }, []);

    React.useEffect(() => { if (open) { load(); setError(''); } }, [open, load]);

    const reset = () => { setForm(EMPTY); setEditId(null); };

    const save = async () => {
        if (!form.text.trim()) { setError('Write the update text first'); return; }
        setBusy(true); setError('');
        try {
            if (editId) await axios.put(`${API}/${editId}`, form);
            else await axios.post(API, { ...form, active: true });
            reset(); await load(); notify();
        } catch (e) { setError(e.response?.data?.message || e.message); }
        setBusy(false);
    };

    const setActive = async (item, active) => {
        try { await axios.put(`${API}/${item.id}`, { active }); await load(); notify(); }
        catch (e) { setError(e.response?.data?.message || e.message); }
    };

    const remove = async (item) => {
        if (!window.confirm(`Delete this update?\n\n${item.text}`)) return;
        try { await axios.delete(`${API}/${item.id}`); if (editId === item.id) reset(); await load(); notify(); }
        catch (e) { setError(e.response?.data?.message || e.message); }
    };

    const startEdit = (item) => {
        setEditId(item.id);
        setForm({ text: item.text, tag: item.tag || '', roles: item.roles || [], segments: item.segments || [] });
    };

    const everyone = !form.roles.length && !form.segments.length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Publish CRM Updates</DialogTitle>
            <DialogContent dividers>
                <TextField
                    label="Update text"
                    placeholder="e.g. New: Category-wise Sale card on your dashboard"
                    fullWidth
                    multiline
                    minRows={2}
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    inputProps={{ maxLength: 500 }}
                    sx={{ mb: 2 }}
                />
                <TextField
                    label="Tag (top-right of the text)"
                    placeholder="New / Changed / Fix — blank shows the date"
                    size="small"
                    value={form.tag}
                    onChange={(e) => setForm({ ...form, tag: e.target.value })}
                    inputProps={{ maxLength: 20 }}
                    sx={{ mb: 2, width: 320 }}
                />

                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                    Show to {everyone && <Chip size="small" color="primary" label="Everyone" sx={{ ml: 1 }} />}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    Pick any roles and/or teams; a user sees it if they match any one. Nothing picked = everyone. Each update shows for 24 hours from publish.
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Teams</Typography>
                    {SEGMENT_OPTIONS.map((s) => (
                        <FormControlLabel
                            key={s.value}
                            control={<Checkbox size="small" checked={form.segments.includes(s.value)} onChange={() => setForm({ ...form, segments: toggle(form.segments, s.value) })} />}
                            label={s.label}
                        />
                    ))}
                </Box>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Roles (userType)</Typography>
                    {ROLE_OPTIONS.map((r) => (
                        <FormControlLabel
                            key={r.value}
                            sx={{ width: 210 }}
                            control={<Checkbox size="small" checked={form.roles.includes(r.value)} onChange={() => setForm({ ...form, roles: toggle(form.roles, r.value) })} />}
                            label={`${r.label} (${r.value})`}
                        />
                    ))}
                </Box>

                {error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>}
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button variant="contained" color="primary" disabled={busy} onClick={save}>
                        {editId ? 'Save changes' : 'Publish'}
                    </Button>
                    {editId && <Button onClick={reset}>Cancel edit</Button>}
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Published ({items.length})</Typography>
                {!items.length && <Typography variant="body2" color="textSecondary">Nothing published yet.</Typography>}
                {items.map((i) => (
                    <Box key={i.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, borderBottom: '1px solid #eee', opacity: i.active && !isExpired(i) ? 1 : 0.55 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                                {i.text} {i.tag && <Chip size="small" label={i.tag} sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
                            </Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {isExpired(i) && <Chip size="small" label="Expired (24h)" />}
                                {!(i.roles || []).length && !(i.segments || []).length && <Chip size="small" variant="outlined" label="Everyone" />}
                                {(i.segments || []).map((s) => <Chip key={s} size="small" variant="outlined" color="primary" label={segLabel(s)} />)}
                                {(i.roles || []).map((r) => <Chip key={r} size="small" variant="outlined" label={roleLabel(r)} />)}
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1, alignSelf: 'center' }}>
                                    {new Date(i.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                            </Box>
                        </Box>
                        <FormControlLabel
                            control={<Switch size="small" checked={!!i.active} onChange={(e) => setActive(i, e.target.checked)} />}
                            label={i.active ? 'Live' : 'Off'}
                        />
                        <IconButton size="small" onClick={() => startEdit(i)}><IconEdit size="1.1rem" /></IconButton>
                        <IconButton size="small" onClick={() => remove(i)}><IconTrash size="1.1rem" /></IconButton>
                    </Box>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CrmUpdatesManager;
